/**
 * Frame geometry for an in-page detached surface (架构方案 §5.8, 见 18 弹窗拦截),
 * kept apart from the component so the rules are testable without a DOM — the
 * same split every other pure bit of this package uses.
 *
 * These are the decisions an OS window manager would have made for us: how big a
 * window may be given the screen it lands on, and how far it may be dragged
 * before it can no longer be grabbed back.
 */

/** Height of the frame's own title bar; also its height while collapsed. */
export const FRAME_BAR_HEIGHT = 28;
/** How much of a frame must stay on screen when dragged towards an edge. */
export const KEEP_ON_SCREEN = 80;
/** Offset between frames so a second one does not land exactly on the first. */
const CASCADE_STEP = 24;
/** Size when the request named none — the desktop `BrowserWindow` default. */
const DEFAULT_WIDTH = 1024;
const DEFAULT_HEIGHT = 720;
/** Resize floor when the request named none, as `WindowManager` has. */
export const FALLBACK_MIN_WIDTH = 500;
export const FALLBACK_MIN_HEIGHT = 400;

export interface Viewport {
  width: number;
  height: number;
}

export interface FrameBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where a frame starts out: the requested size (plus the bar, so the surface
 * still gets the height it asked for), centred, never larger than the viewport,
 * and cascaded by its position in the stack.
 */
export function initialFrameBox(
  request: { width?: number; height?: number },
  cascade: number,
  viewport: Viewport,
): FrameBox {
  const width = Math.min(
    request.width ?? DEFAULT_WIDTH,
    Math.max(viewport.width - KEEP_ON_SCREEN, 1),
  );
  const height = Math.min(
    (request.height ?? DEFAULT_HEIGHT) + FRAME_BAR_HEIGHT,
    Math.max(viewport.height - KEEP_ON_SCREEN, 1),
  );
  const offset = cascade * CASCADE_STEP;
  return {
    x: clamp((viewport.width - width) / 2 + offset, 0, viewport.width - width),
    y: clamp(
      (viewport.height - height) / 2 + offset,
      0,
      viewport.height - height,
    ),
    width,
    height,
  };
}

/**
 * Drag by a pointer delta. A frame may hang off any edge, but never so far that
 * its title bar — the only way to drag it back — leaves the viewport.
 */
export function moveFrame(
  from: FrameBox,
  dx: number,
  dy: number,
  viewport: Viewport,
): FrameBox {
  return {
    ...from,
    x: clamp(
      from.x + dx,
      KEEP_ON_SCREEN - from.width,
      viewport.width - KEEP_ON_SCREEN,
    ),
    y: clamp(from.y + dy, 0, viewport.height - FRAME_BAR_HEIGHT),
  };
}

/** Resize from the bottom-right corner, honouring the requested floors. */
export function resizeFrame(
  from: FrameBox,
  dx: number,
  dy: number,
  min: { width: number; height: number },
): FrameBox {
  return {
    ...from,
    width: Math.max(min.width, from.width + dx),
    height: Math.max(min.height, from.height + dy),
  };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), Math.max(min, max));
}
